import { WizardCompanyData } from '@/mocks/wizard';

interface StepCompanyProps {
  data: WizardCompanyData;
  onChange: (data: WizardCompanyData) => void;
}

const COMPANY_TYPES = ['СЕС', 'BESS', 'СЕС + BESS', 'ВЕС', 'Гідро', 'Агрегатор'];

const inputCls = 'w-full px-3.5 py-2.5 rounded-lg text-sm border transition-all outline-none focus:border-gray-400';
const inputStyle = { background: '#FAFAFA', borderColor: '#E5E5E8', color: '#1A1A1A', fontFamily: 'Inter, sans-serif' };

export default function StepCompany({ data, onChange }: StepCompanyProps) {
  const set = (field: keyof WizardCompanyData, value: string) =>
    onChange({ ...data, [field]: value });

  return (
    <div className="max-w-2xl mx-auto w-full">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#1A1A1A', letterSpacing: '-0.02em' }}>
          Дані компанії
        </h2>
        <p className="text-sm" style={{ color: '#8B8B94' }}>
          Інформація про вашу організацію та об&apos;єкт генерації
        </p>
      </div>

      {/* Company type selector */}
      <div className="mb-6">
        <label className="block text-xs font-semibold mb-2" style={{ color: '#1A1A1A' }}>
          Тип об&apos;єкта *
        </label>
        <div className="flex flex-wrap gap-2">
          {COMPANY_TYPES.map((type) => {
            const isActive = data.type === type;
            return (
              <button
                key={type}
                type="button"
                onClick={() => set('type', type)}
                className="px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-all whitespace-nowrap"
                style={{
                  background: isActive ? '#1A1A1A' : '#FAFAFA',
                  color: isActive ? '#fff' : '#6B6B74',
                  border: `1.5px solid ${isActive ? '#1A1A1A' : '#E5E5E8'}`,
                }}
              >
                {type}
              </button>
            );
          })}
        </div>
      </div>

      {/* Legal info */}
      <div
        className="p-4 rounded-xl mb-6"
        style={{ background: '#F7F7F9', border: '1px solid #E5E5E8' }}
      >
        <p className="text-xs font-semibold mb-3" style={{ color: '#8B8B94', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Юридична інформація
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#1A1A1A' }}>
              Повна назва компанії *
            </label>
            <input
              type="text"
              value={data.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="ТОВ Болград Еліос"
              className={inputCls}
              style={inputStyle}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#1A1A1A' }}>
              ЄДРПОУ *
            </label>
            <input
              type="text"
              value={data.edrpou}
              onChange={(e) => set('edrpou', e.target.value)}
              placeholder="12345678"
              maxLength={8}
              className={inputCls}
              style={inputStyle}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#1A1A1A' }}>
              Веб-сайт
            </label>
            <input
              type="url"
              value={data.website}
              onChange={(e) => set('website', e.target.value)}
              placeholder="https://bolgrad-elios.com.ua"
              className={inputCls}
              style={inputStyle}
            />
          </div>
        </div>
      </div>

      {/* Location */}
      <div
        className="p-4 rounded-xl mb-6"
        style={{ background: '#F7F7F9', border: '1px solid #E5E5E8' }}
      >
        <p className="text-xs font-semibold mb-3" style={{ color: '#8B8B94', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Розташування об&apos;єкта
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#1A1A1A' }}>
              Місто / Населений пункт
            </label>
            <input
              type="text"
              value={data.city}
              onChange={(e) => set('city', e.target.value)}
              placeholder="Болград"
              className={inputCls}
              style={inputStyle}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#1A1A1A' }}>
              Адреса об&apos;єкта
            </label>
            <input
              type="text"
              value={data.address}
              onChange={(e) => set('address', e.target.value)}
              placeholder="вул. Сонячна, 1"
              className={inputCls}
              style={inputStyle}
            />
          </div>
        </div>
      </div>

      {/* Technical */}
      <div
        className="p-4 rounded-xl"
        style={{ background: '#F7F7F9', border: '1px solid #E5E5E8' }}
      >
        <p className="text-xs font-semibold mb-3" style={{ color: '#8B8B94', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Технічні параметри
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#1A1A1A' }}>
              Встановлена потужність (МВт)
            </label>
            <input
              type="number"
              value={data.capacity}
              onChange={(e) => set('capacity', e.target.value)}
              placeholder="5.0"
              step="0.1"
              min="0"
              className={inputCls}
              style={inputStyle}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#1A1A1A' }}>
              Часовий пояс об&apos;єкта
            </label>
            <select
              value={data.timezone}
              onChange={(e) => set('timezone', e.target.value)}
              className={inputCls}
              style={inputStyle}
            >
              <option value="Europe/Kiev">Europe/Kiev (UTC+3)</option>
              <option value="Europe/Warsaw">Europe/Warsaw (UTC+2)</option>
              <option value="UTC">UTC</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}
